import { useMemo } from 'react';

/* Suggested path: frontend/src/components/ContourBG.jsx
   -----------------------------------------------------
   Shared topographic-contour background — the Atlas signature.
   Used by Dashboard, Skills, Billing, Login, Signup, Settings, Contact,
   Feedback, Privacy, Terms and NotFound (11 pages). Landing does NOT use
   this component — it replaced it with its own local `GridBG`, so
   retuning this file does not touch Landing at all.

   size="hero"    → 1440×900 viewBox, 7 rows + crosshair ticks on the mark
   size="wide"    → 1440×480 viewBox, same paths as the legal-page headers
   size="compact" → 1440×280 viewBox, for slimmer page headers (Dashboard/Skills)

   Retuned for the cartographer's chart aesthetic (Atlas redesign, Phase 2):
   hairlines shifted from a neutral off-white/near-black to a cool slate-grey
   so they read as survey ink rather than a generic UI grid, and a radial
   mask now fades every hairline out before it reaches the SVG's edges — the
   previous version drew full-bleed edge-to-edge, which read as a tiled
   pattern rather than a chart with a boundary. The orange survey mark is
   deliberately left outside the mask: it is the one fixed, always-legible
   point on the sheet, not part of the fading field. */
const HERO_ROWS = [0, 1, 2, 3, 4, 5, 6];
const WIDE_ROWS = [0, 1, 2, 3, 4];
const COMPACT_ROWS = [0, 1, 2, 3];

let instanceCount = 0;

const ContourBG = ({ isDark, opacity = 1, size = 'wide' }) => {
  const isHero = size === 'hero';
  const isCompact = size === 'compact';
  const H = isHero ? 900 : isCompact ? 280 : 480;
  const rows = isHero ? HERO_ROWS : isCompact ? COMPACT_ROWS : WIDE_ROWS;
  // Cool slate-grey, not neutral off-white/off-black — the hairline should
  // read as ink on a survey sheet, not a UI grid line.
  const stroke = isDark ? 'rgba(148,163,184,0.07)' : 'rgba(71,85,105,0.08)';
  const accent = isDark ? 'rgba(234,88,12,0.25)' : 'rgba(234,88,12,0.3)';
  const markCx = 1180;
  const markCy = isHero ? 170 : isCompact ? 76 : 120;
  const markR = isHero ? 52 : isCompact ? 28 : 44;
  const markR2 = isHero ? 34 : isCompact ? 18 : 28;

  // Stable per-mount id so multiple instances on one page never collide,
  // without pulling in React 18's useId just for this.
  const maskId = useMemo(() => `contourbg-fade-${++instanceCount}`, []);

  return (
    <svg viewBox={`0 0 1440 ${H}`} preserveAspectRatio="xMidYMid slice"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, opacity }}>
      <defs>
        <radialGradient id={maskId} cx="50%" cy="50%" r="62%">
          <stop offset="0%" stopColor="#fff" />
          <stop offset="60%" stopColor="#fff" />
          <stop offset="100%" stopColor="#000" />
        </radialGradient>
        <mask id={`${maskId}-mask`} maskUnits="objectBoundingBox">
          <rect x="0" y="0" width="1440" height={H} fill={`url(#${maskId})`} />
        </mask>
      </defs>
      <g mask={`url(#${maskId}-mask)`}>
        {rows.map((i) => (
          <path key={i} fill="none" stroke={stroke} strokeWidth="1.2"
            d={isHero
              ? `M -100 ${180 + i * 95} C 260 ${90 + i * 88}, 480 ${300 + i * 72}, 760 ${190 + i * 90} S 1220 ${70 + i * 95}, 1540 ${230 + i * 85}`
              : isCompact
              ? `M -100 ${70 + i * 62} C 260 ${26 + i * 58}, 480 ${118 + i * 46}, 760 ${70 + i * 58} S 1220 ${20 + i * 62}, 1540 ${88 + i * 54}`
              : `M -100 ${110 + i * 80} C 260 ${40 + i * 74}, 480 ${190 + i * 60}, 760 ${110 + i * 76} S 1220 ${30 + i * 80}, 1540 ${140 + i * 72}`}
          />
        ))}
      </g>
      <circle cx={markCx} cy={markCy} r={markR} fill="none" stroke={isDark ? 'rgba(234,88,12,0.16)' : 'rgba(234,88,12,0.18)'} strokeWidth="1.2" />
      <circle cx={markCx} cy={markCy} r={markR2} fill="none" stroke={isDark ? 'rgba(234,88,12,0.12)' : 'rgba(234,88,12,0.13)'} strokeWidth="1" />
      <circle cx={markCx} cy={markCy} r="3" fill="#ea580c" opacity="0.5" />
      {isHero && (
        <path d={`M ${markCx} ${markCy - 62} v 12 M ${markCx} ${markCy + 50} v 12 M ${markCx - 62} ${markCy} h 12 M ${markCx + 50} ${markCy} h 12`}
          stroke={accent} strokeWidth="1.2" />
      )}
    </svg>
  );
};

export default ContourBG;
